import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import buildingImg from '../assets/building.png';
import { supabase } from '../supabaseClient';
import SEO from '../components/SEO';

const Careers = () => {
  const [vacancies, setVacancies] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Application Modal State
  const [selectedJob, setSelectedJob] = useState(null);
  const [formData, setFormData] = useState({ name: '', email: '' });
  const [resumeFile, setResumeFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchVacancies();
  }, []);

  const fetchVacancies = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('careers')
        .select('*')
        .eq('is_active', true)
        .order('created_at', { ascending: false });
      if (!error) setVacancies(data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleApplyClick = (job) => {
    setSelectedJob(job);
    setFormData({ name: '', email: '' });
    setResumeFile(null);
  };

  const handleSubmitApplication = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !resumeFile) {
      alert("Please fill in all fields and select a resume.");
      return;
    }

    setSubmitting(true);
    try {
      // 1. Upload Resume
      const fileExt = resumeFile.name.split('.').pop();
      const fileName = `${Date.now()}_${Math.random().toString(36).substring(7)}.${fileExt}`;
      const filePath = `resumes/${fileName}`;
      
      const { error: uploadError } = await supabase.storage
        .from('hbb_files')
        .upload(filePath, resumeFile);
        
      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('hbb_files')
        .getPublicUrl(filePath);

      // 2. Insert into resumes table
      const { error: dbError } = await supabase
        .from('resumes')
        .insert([{
          career_id: selectedJob.id,
          applicant_name: formData.name,
          email: formData.email,
          resume_url: publicUrl
        }]);

      if (dbError) throw dbError;

      alert("Application submitted successfully!");
      setSelectedJob(null);
    } catch (error) {
      console.error('Application error:', error);
      alert('Failed to submit application: ' + error.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-[#041424] min-h-screen w-full font-sans text-white">
      <SEO 
        title="Careers | HBB0011 REALTY"
        description="Build your career and shape the future of real estate with HBB0011 REALTY. Explore our current vacancies and join our visionary team."
        keywords="careers at HBB0011 REALTY, real estate jobs, architectural careers, join our team"
      />
      
      {/* 1. Hero Section (No Founder Image) */}
      <section className="relative w-full pt-32 pb-24 md:pt-40 md:pb-32 lg:min-h-[70vh] flex items-center overflow-hidden border-b border-gray-800">
        <div className="absolute inset-0 z-0">
          <img src={buildingImg} alt="Luxury Corporate Architecture" className="w-full h-full object-cover opacity-20 mix-blend-luminosity" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#041424] via-[#041424]/90 lg:via-[#041424]/40 to-transparent"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-[#041424] via-transparent to-transparent"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="max-w-3xl text-center lg:text-left">
            <div className="flex items-center justify-center lg:justify-start gap-2 text-xs sm:text-sm font-bold tracking-widest mb-6 uppercase">
              <Link to="/" className="text-white hover:text-[#D4AF37]">Home</Link>
              <span className="text-gray-500 material-symbols-outlined text-sm">chevron_right</span>
              <span className="text-[#D4AF37]">Careers</span>
            </div>
            
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight tracking-tight uppercase">
              Build Your Career.<br/>
              <span className="text-[#D4AF37]">Shape the Future.</span>
            </h1>
            
            <p className="text-sm sm:text-base text-gray-400 mb-10 max-w-xl mx-auto lg:mx-0 leading-relaxed">
              At HBB0011 Realty Realtors, we foster a culture of trust, innovation, and growth. Join a visionary team where every individual has the opportunity to excel and make a meaningful impact.
            </p>
          </div>
        </div>
      </section>

      {/* 2. Stats Banner */}
      <section className="py-12 bg-[#06121e] border-b border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-2 md:grid-cols-5 gap-8 divide-x divide-gray-800">
          {[
            { icon: 'groups', val: '500+', label: 'Happy Team Members' },
            { icon: 'military_tech', val: '1+', label: 'Years of Excellence' },
            { icon: 'trending_up', val: '50+', label: 'Career Opportunities' },
            { icon: 'menu_book', val: 'Continuous', label: 'Learning & Growth' },
            { icon: 'handshake', val: 'People', label: 'First Culture' }
          ].map((stat, i) => (
            <div key={i} className="flex flex-col items-center text-center px-2">
              <span className="material-symbols-outlined text-[#D4AF37] text-3xl font-light mb-3">{stat.icon}</span>
              <p className="text-xl sm:text-2xl font-bold text-white mb-1">{stat.val}</p>
              <p className="text-[9px] sm:text-[10px] text-gray-500 uppercase tracking-widest">{stat.label}</p>
            </div>
          ))}
        </div>
      </section>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        
        {/* 3. Why Work With Us & Benefits Grid */}
        <section className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-32">
          <div className="lg:col-span-7 bg-[#06121e] border border-gray-800 p-8 sm:p-12 rounded-sm">
            <h2 className="text-xl sm:text-2xl text-white font-bold mb-8 uppercase tracking-widest">Why Work With Us?</h2>
            <ul className="space-y-6">
              {[
                'Work on iconic projects across prime locations',
                'Collaborate with industry experts',
                'Transparent culture and open communication',
                'Rewards, recognition and career growth',
                'Training programs to enhance your skills'
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-4 text-gray-300 text-sm">
                  <span className="material-symbols-outlined text-[#D4AF37] mt-0.5">check_circle</span>
                  {item}
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-5 grid grid-cols-2 gap-4">
            {['Career Growth', 'Learning & Development', 'Health Insurance', 'Paid Time Off', 'Performance Bonuses', 'Team Celebrations'].map((b, i) => (
              <div key={i} className="bg-[#06121e] border border-gray-800 p-6 rounded-sm flex flex-col items-center text-center hover:border-[#D4AF37]/50 transition-colors">
                <span className="material-symbols-outlined text-[#D4AF37] text-3xl mb-3">verified</span>
                <span className="text-[10px] text-white font-bold uppercase tracking-widest">{b}</span>
              </div>
            ))}
          </div>
        </section>

        {/* 4. Current Openings */}
        <section className="mb-24" id="openings">
          <div className="flex flex-col sm:flex-row justify-between items-center mb-12 border-b border-gray-800 pb-6 gap-4">
            <h2 className="text-2xl text-white font-bold uppercase tracking-widest">Current Openings</h2>
          </div>
          
          {loading ? (
            <p className="text-center text-gray-500 py-8">Loading current openings...</p>
          ) : vacancies.length === 0 ? (
            <div className="bg-[#06121e] border border-gray-800 rounded-sm p-12 text-center">
              <span className="material-symbols-outlined text-[#D4AF37] text-4xl mb-4">work_off</span>
              <h3 className="text-white font-bold text-base uppercase tracking-widest mb-2">No vacancies available right now</h3>
              <p className="text-gray-400 text-sm max-w-md mx-auto">Please check back later or send us your resume for future opportunities at info@hbb0011realty.com.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {vacancies.map(job => (
                <div key={job.id} className="bg-[#06121e] border border-gray-800 p-6 rounded-sm flex flex-col hover:border-[#D4AF37]/50 transition-colors">
                  <h3 className="text-xl font-bold text-white mb-2">{job.title}</h3>
                  <p className="text-gray-400 text-sm mb-4 line-clamp-3 flex-1">{job.description}</p>
                  <button 
                    onClick={() => handleApplyClick(job)}
                    className="mt-auto border border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#041424] font-bold text-xs uppercase tracking-widest py-3 px-6 rounded-sm transition-colors w-full flex items-center justify-center gap-2"
                  >
                    Apply Now <span className="material-symbols-outlined text-sm">arrow_forward</span>
                  </button>
                </div>
              ))}
            </div>
          )}
        </section>

      </main>

      {/* 5. CTA Section */}
      <section className="bg-[#06121e] py-20 border-t border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row items-center justify-between gap-8">
          <div>
            <h2 className="text-2xl sm:text-3xl text-white font-light uppercase mb-2">Be a part of our <span className="font-bold">growing team</span></h2>
            <p className="text-gray-400 text-sm">Join HBB0011 Realty Realtors and help us build a better tomorrow.</p>
          </div>
          <button className="bg-[#D4AF37] text-[#041424] font-bold text-xs uppercase tracking-widest py-4 px-8 rounded-sm hover:bg-white transition-colors flex items-center gap-2">
            Explore Opportunities <span className="material-symbols-outlined text-sm">arrow_forward</span>
          </button>
        </div>
      </section>

      {/* Application Modal */}
      {selectedJob && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm overflow-y-auto">
          <div className="bg-[#06121e] border border-gray-800 rounded-lg p-6 sm:p-8 w-full max-w-md relative my-8">
            <button 
              onClick={() => !submitting && setSelectedJob(null)}
              className="absolute top-4 right-4 text-gray-400 hover:text-white"
              disabled={submitting}
            >
              <span className="material-symbols-outlined">close</span>
            </button>
            <h3 className="text-2xl font-bold text-white mb-2 tracking-wide">Apply For Role</h3>
            <p className="text-[#D4AF37] text-sm font-bold uppercase tracking-widest mb-6">{selectedJob.title}</p>
            
            <form onSubmit={handleSubmitApplication} className="flex flex-col gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-1">Full Name</label>
                <input 
                  type="text" 
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="bg-[#041424] border border-gray-800 rounded px-4 py-3 text-white w-full focus:outline-none focus:border-[#D4AF37]" 
                  required 
                  disabled={submitting}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-1">Email Address</label>
                <input 
                  type="email" 
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className="bg-[#041424] border border-gray-800 rounded px-4 py-3 text-white w-full focus:outline-none focus:border-[#D4AF37]" 
                  required 
                  disabled={submitting}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-1">Resume (PDF/DOC)</label>
                <input 
                  type="file" 
                  accept=".pdf,.doc,.docx"
                  onChange={(e) => setResumeFile(e.target.files[0])}
                  className="bg-[#041424] border border-gray-800 rounded px-4 py-2 text-gray-400 w-full focus:outline-none focus:border-[#D4AF37] file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-xs file:font-bold file:bg-[#D4AF37] file:text-[#041424] hover:file:bg-white transition-all" 
                  required 
                  disabled={submitting}
                />
              </div>
              <button 
                type="submit" 
                disabled={submitting}
                className={`mt-4 font-bold text-xs uppercase tracking-widest py-4 rounded-sm transition-colors flex items-center justify-center gap-2 ${submitting ? 'bg-gray-800 text-gray-500 cursor-not-allowed' : 'bg-[#D4AF37] text-[#041424] hover:bg-white'}`}
              >
                {submitting ? (
                  <><span className="material-symbols-outlined animate-spin text-sm">sync</span> Processing...</>
                ) : (
                  'Submit Application'
                )}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Careers;